import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { isValidValue } from 'assets/utils/parsersAndValidation/Validators';
import { values } from 'lodash';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import LineItem from 'models/enums/LineItem';
import { useClaimEditJobPayeeItem, useClaimJobEdit } from '../ClaimEdit.Job.Hooks';

type Props = {
  data?: Utils.FormData<Model.IClaimJobItem> & { isWarrantyProduct: boolean };
  insurerHeaders?: string[];
  onUpdate(data: Partial<Model.IClaimJobItem>): void;
  countryCode?: Model.Enum.CountryCode;
};

export default function ClaimEditJobItemDeductible({ data, insurerHeaders = [], onUpdate }: Props) {
  const { lang, translate } = getLocales();
  const {
    getJobItemDefaultValues,
    isClaimJobItemEditable,
    isClaimAdmin,
    permissions,
    contract,
    isClaimOwner,
    claimJob,
    canEditAdjustedAmt,
    canEditInsurerAdjudications,
  } = useClaimJobEdit();
  const { setOffset, setSearch, limit, offset, paginatedPayees, searchedPayees } = useClaimEditJobPayeeItem();

  const payee = new ClaimJobItemPayee(data?.claimJobItemPayee);

  const payeeId =
    data?.claimJobItemPayee?.payee?.id ?? data?.claimJobItemPayee?.client?.id ?? data?.claimJobItemPayee?.dealer?.id;
  const selectInputValue = payeeId ? `${payeeId}-${data?.claimJobItemPayee?.payeeType?.id}` : undefined;

  const isEditable =
    isClaimJobItemEditable ||
    !(isClaimAdmin || (isClaimOwner && (data?.id ? permissions?.EDIT_CLAIMJOBITEM : permissions?.CREATE_CLAIMJOBITEM)));
  const insurerAdjudications = data?.insurerAdjudications ?? {};
  const insurerTotal = values(insurerAdjudications).reduce((sum, value) => sum + (value?.amount ?? 0), 0);

  return (
    <tr>
      <td>
        {payee &&
        data?.id &&
        (claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_WAITING_ON_INVOICE || isEditable) ? (
          <span title={payee.displayInfo?.title}>{payee.displayInfo?.title}</span>
        ) : (
          <SelectInput
            name="claimJobItemPayee"
            data={paginatedPayees?.map((it) => ({ ...it.displayInfo, id: `${it.displayInfo.id}-${it.payeeType?.id}` }))}
            displayText={payee?.displayInfo?.title}
            value={selectInputValue}
            limit={limit}
            offset={offset}
            count={searchedPayees?.length}
            animateOut={() => {
              setOffset(0);
            }}
            error={!data?.claimJobItemPayee && data.jobItemTypeCode === LineItem.deductible && lang.mustNotBeEmpty}
            onSearch={setSearch}
            onOffsetChange={({ value: offset }) => setOffset(offset)}
            disabled={isEditable}
            onChange={({ claimJobItemPayee: id }) => {
              const itemPayee = paginatedPayees?.find(
                (it) => `${it.payee?.id ?? it.dealer?.id ?? it.client?.id}-${it.payeeType?.id}` === id
              );
              onUpdate({
                claimJobItemPayee: itemPayee,
                ...getJobItemDefaultValues({ jobItemTypeCode: data?.jobItemTypeCode, claimJobItemPayee: itemPayee }),
                correction: contract?.deductible ?? 0.0,
              });
              setOffset(0); // Reset offset
              setSearch(undefined); // Reset search
            }}
            style={{
              input: {
                fontSize: Theme.FontSize.S,
              },
            }}
          />
        )}
      </td>
      <td style={{ width: '75px', textAlign: 'center' }}>{translate(data?.statusCode)}</td>
      <td>
        <NumberInput
          name="correction"
          value={data?.correction}
          onChange={onUpdate}
          step={0.01}
          disabled={isEditable || (data.jobItemTypeCode === LineItem.deductible && data?.isWarrantyProduct)}
          style={{ input: { textAlign: 'right' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      <td>
        <NumberInput
          name="adjudication"
          value={data?.adjudication}
          onChange={onUpdate}
          step={0.01}
          disabled={isEditable || !canEditAdjustedAmt}
          style={{ input: { textAlign: 'right' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      {insurerHeaders.map((insurerKey) => {
        const value = insurerAdjudications[insurerKey];
        const isInsurerAmountMissing = !isValidValue(value?.amount);
        const isInsurerTotalMismatched = Math.abs(insurerTotal - (data?.adjudication ?? 0)) > 0.009;

        return (
          <td key={insurerKey} style={{ width: 'auto' }}>
            <NumberInput
              name="insurerAmount"
              value={value?.amount ?? undefined}
              step={0.01}
              error={
                canEditInsurerAdjudications &&
                (isInsurerAmountMissing
                  ? lang.mustNotBeEmpty
                  : isInsurerTotalMismatched &&
                    lang.insurerAdjudicationTotalMustMatchAdjustedAmt.replace(
                      '{amount}',
                      (data?.adjudication ?? 0).toFixed(2)
                    ))
              }
              disabled={isEditable || !canEditInsurerAdjudications}
              onChange={({ insurerAmount }) => {
                onUpdate({
                  insurerAdjudications: {
                    ...insurerAdjudications,
                    [insurerKey]: {
                      ...value,
                      amount: insurerAmount,
                      currency: value?.currency ?? data?.currency,
                    },
                  } as Model.IInsurerAdjudication,
                });
              }}
              style={{ input: { textAlign: 'right' } }}
              iconBefore={<Icon class="fas-dollar-sign" />}
            />
          </td>
        );
      })}
      <td style={{ width: 'auto' }}>
        <NumberInput
          name="total"
          value={data?.adjudication}
          readOnly
          onChange={onUpdate}
          style={{ input: { textAlign: 'right' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
    </tr>
  );
}
